/**
 * KAN-883: быстрые периоды по дням (Сегодня, Вчера, 7 дней, 30 дней) по ключу:
 * период по умолчанию "Лиды по дням" (30 дней) и сопоставление периода с пунктом.
 * Сам период берётся из crmRangePresets (одни правила с окном выбора дат).
 * Быстрые кнопки CrmPeriodControl: набор пунктов (PeriodChoice) и подсвеченный пункт.
 * KAN-963: periodButtonLabel - подпись кнопки календаря (быстрые пункты под ней).
 */
import { crmRangePresets, formatCrmRange } from './crmRange';
import type { CrmRange } from './crmTable';

export const PERIOD_PRESET_LABELS = {
  today: 'Сегодня',
  yesterday: 'Вчера',
  week: '7 дней',
  month: '30 дней',
} as const;

export type PeriodPreset = keyof typeof PERIOD_PRESET_LABELS;

export const PERIOD_PRESETS = Object.keys(PERIOD_PRESET_LABELS) as PeriodPreset[];

/** Период быстрого пункта от сегодняшнего дня по Москве. */
export function periodPresetRange(preset: PeriodPreset, today: string): CrmRange {
  const found = crmRangePresets(today).find((p) => p.label === PERIOD_PRESET_LABELS[preset]);
  if (!found) throw new Error(`нет быстрого периода ${preset}`);
  return found.range;
}

/** Быстрый пункт, совпадающий с периодом, или null ("Свой период"). */
export function matchPeriodPreset(range: CrmRange, today: string): PeriodPreset | null {
  return (
    PERIOD_PRESETS.find((preset) => {
      const r = periodPresetRange(preset, today);
      return r.from === range.from && r.to === range.to;
    }) ?? null
  );
}

/** Пункт быстрых кнопок: период или "Всё время" (без периода). */
export type PeriodChoice = PeriodPreset | 'all';

export const ALL_TIME_LABEL = 'Всё время';

export function periodChoiceLabel(choice: PeriodChoice): string {
  return choice === 'all' ? ALL_TIME_LABEL : PERIOD_PRESET_LABELS[choice];
}

/**
 * KAN-963: подпись кнопки календаря: "Всё время", имя многодневного быстрого
 * периода ("7 дней", "30 дней"), иначе даты ("29 октября", "1 - 9 октября").
 */
export function periodButtonLabel(range: CrmRange | null, today: string): string {
  if (!range) return ALL_TIME_LABEL;
  const preset = range.from === range.to ? null : matchPeriodPreset(range, today);
  return preset ? PERIOD_PRESET_LABELS[preset] : formatCrmRange(range, today);
}

/** Подсвеченная кнопка: "Всё время" при range null, иначе совпадающий быстрый пункт из набора. */
export function activePeriodChoice(
  range: CrmRange | null,
  choices: readonly PeriodChoice[],
  today: string
): PeriodChoice | null {
  if (!range) return choices.includes('all') ? 'all' : null;
  return (
    choices.find((choice) => {
      if (choice === 'all') return false;
      const r = periodPresetRange(choice, today);
      return r.from === range.from && r.to === range.to;
    }) ?? null
  );
}
